/**
 * Der synchronisierte Matchzustand.
 *
 * Alles, was in diesem Zustand steht, schickt der Server automatisch an alle
 * verbundenen Clients - und zwar nur die Aenderungen, nicht jedes Mal alles.
 * Der Client kann diesen Zustand ausschliesslich LESEN.
 *
 * Stand v0.5: Spieler mit Hero, Skills, Portal und Wirtschaft sowie
 * angreifende Creeps. Wellen und Defender kommen in den folgenden Meilensteinen.
 *
 * Wichtig zum Verstaendnis der Lanes:
 * Jeder Spieler verteidigt seine EIGENE Lane. Beide Lanes benutzen dieselbe
 * Geometrie, und alle Positionen sind deshalb LANE-LOKAL: x = 0 ist immer der
 * Spawnpunkt der Angreifer, x = hoch immer das verteidigte Portal.
 * Wem eine Lane gehoert, sagt das Feld laneOwner.
 */

import { schema, t, type SchemaType } from '@colyseus/schema';

/**
 * Zustand eines einzelnen Skills des Heroes.
 *
 * Uebertragen wird nur, was die Oberflaeche braucht: welcher Skill es ist und
 * wie lange er noch abklingt. Kosten, Reichweiten und Wirkung stehen in
 * @dueline/game-data und aendern sich waehrend eines Matches nicht.
 */
export const SkillState = schema(
  {
    /** ID des Skills, z. B. shield_slam. */
    skillId: t.string(),
    /** 1 bis 3 sind die Grundskills, 4 ist das Ultimate. */
    slot: t.uint8(),
    /** Verbleibende Abklingzeit in Sekunden. 0 = bereit. */
    cooldownRemaining: t.float32().default(0),
  },
  'SkillState',
);
export type SkillState = SchemaType<typeof SkillState>;

/**
 * Ausbaustufen der drei Grundskills (Abschnitt 46).
 *
 * Je Skill hoechstens zwei Stufen. Das Ultimate wird nicht ausgebaut - es
 * kommt auf Level 5 von selbst.
 */
export const HeroSkillUpgrades = schema(
  {
    slot1: t.uint8().default(0),
    slot2: t.uint8().default(0),
    slot3: t.uint8().default(0),
  },
  'HeroSkillUpgrades',
);
export type HeroSkillUpgrades = SchemaType<typeof HeroSkillUpgrades>;

/**
 * Ein Effekt am Boden - zum Beispiel der zaehe Boden nach Groundbreaker
 * (Skilltree, Abschnitt 46).
 *
 * Er steht im Zustand, damit der Client ihn zeichnen kann. Gewirkt wird er
 * ausschliesslich auf dem Server.
 */
export const GroundEffectState = schema(
  {
    id: t.string(),
    /**
     * Wem der Effekt gehoert.
     *
     * Es gibt nur noch EINE Lane - der Besitzer sagt deshalb nicht mehr,
     * wo der Effekt liegt, sondern nur noch, wen er bremst (die Creeps,
     * die auf ihn zulaufen) und in welcher Farbe er gezeigt wird.
     */
    ownerSessionId: t.string(),
    /**
     * Welche Art Effekt:
     *
     *   slow  - zaeher Boden, bremst alles darin
     *   echo  - Ankuendigung eines Einschlags, wirkt auf niemanden
     *   smoke - die Wolke der Smoke Bomb, reine Anzeige
     *   trap      - eine gelegte Falle (22.10.2026), siehe armingSeconds
     *   trap_snap - genau jetzt ist sie zugeschnappt
     *
     * "trap_snap" ist der Augenblick fuer die Optik: Die Falle bleibt dafuer
     * kurz liegen und wechselt nur ihre Art, bevor sie verschwindet. Eine
     * ungenutzt verfallene Falle nimmt diese Art NIE an - daran sind die
     * beiden Faelle zu unterscheiden.
     *
     * Warum nicht ein Zaehler am Spieler (wie lastSkillSeq): Ein
     * Colyseus-Schema darf hoechstens 63 Felder haben, und PlayerState ist
     * voll. Siehe den Hinweis dort.
     */
    kind: t.string().default('slow'),
    x: t.float32().default(0),
    y: t.float32().default(0),
    radius: t.float32().default(0),
    secondsRemaining: t.float32().default(0),
    /** Restliches Tempo der Gegner darin, 0 bis 1. Nur bei kind "slow". */
    slowFactor: t.float32().default(1),
    /**
     * Sekunden, bis eine Falle scharf ist. 0 heisst: scharf (22.10.2026).
     *
     * Nur bei kind "trap" belegt. Die Optik haengt daran, ob die
     * Baerenfalle noch offen klappt oder schon lauert - und genau dafuer
     * steht es im Zustand: Der Client soll es nicht selbst mitzaehlen
     * muessen.
     */
    armingSeconds: t.float32().default(0),
  },
  'GroundEffectState',
);
export type GroundEffectState = SchemaType<typeof GroundEffectState>;

/**
 * Ein Item im Besitz eines Heroes (Masterprompt Abschnitt 66).
 *
 * Gekauft wird ueberall, geliefert mit Verzoegerung: Solange
 * deliverySecondsRemaining laeuft, ist das Item unterwegs und wirkt noch nicht.
 */
export const ItemState = schema(
  {
    itemId: t.string(),
    /** 1 bis 3. Das Revive Totem bleibt auf 1. */
    stage: t.uint8().default(1),
    /** Bisher eingesetztes Gold - Grundlage fuer den Verkaufswert. */
    investedGold: t.uint32().default(0),
    /** Restliche Lieferzeit in Sekunden. 0 = wirkt. */
    deliverySecondsRemaining: t.float32().default(0),
  },
  'ItemState',
);
export type ItemState = SchemaType<typeof ItemState>;

/**
 * Ein Trank, der noch unterwegs ist (Produktentscheidung vom 05.10.2026).
 *
 * Traenke kamen frueher sofort an, Slot-Items erst nach der Lieferzeit.
 * Damit war der Heiltrank ein Notknopf mitten im Kampf. Jetzt muss man
 * vorher gekauft haben - das ist der eigentliche Sinn einer Lieferzeit.
 *
 * Jeder Kauf bekommt seine EIGENE Restzeit, genau wie bei den Items. Wer
 * zwei kurz hintereinander kauft, bekommt sie auch kurz hintereinander -
 * und nicht beide erst, wenn der zweite dran waere.
 */
export const PendingPotionState = schema(
  {
    consumableId: t.string(),
    /** Restliche Lieferzeit in Sekunden. */
    deliverySecondsRemaining: t.float32().default(0),
  },
  'PendingPotionState',
);
export type PendingPotionState = SchemaType<typeof PendingPotionState>;

/**
 * Ein verbundener Spieler samt seines Heroes und seines Portals.
 *
 * Position und Blickrichtung liegen bewusst DIREKT auf dem Spieler und nicht in
 * einem verschachtelten Objekt: Die Vorhersage auf dem Client spiegelt eine flache
 * Liste einfacher Zahlenfelder EINER Schema-Instanz - flach ist hier also die
 * technisch passende Form.
 */
/*
 * ACHTUNG, DIESES SCHEMA IST VOLL (22.10.2026).
 *
 * Colyseus erlaubt hoechstens 63 Felder je Schema. PlayerState ist am
 * Limit: Ein weiteres Feld laesst das ganze Spiel nicht mehr starten
 * ("Can't define field ... Schema instances may only have up to 63
 * fields"), und zwar sofort beim Laden - nicht erst im Spiel.
 *
 * Wer etwas Neues ueber einen Spieler uebertragen will, hat drei Wege:
 * ein bestehendes Feld mitbenutzen, die Angabe an ein anderes Schema
 * haengen (so macht es die Falle ueber den Bodeneffekt), oder ein
 * Unterschema anlegen.
 */
export const PlayerState = schema(
  {
    /** Von Colyseus vergebene Sitzungs-ID. Zugleich der Name der eigenen Lane. */
    sessionId: t.string(),
    /** "blue" oder "orange" - bestimmt die Brandingfarbe. */
    side: t.string(),
    /** ID des gespielten Heroes. Bis zur Hero-Auswahl (v0.15) immer der Vanguard. */
    heroId: t.string(),
    /** False, solange ein Reconnect aussteht. */
    connected: t.boolean().default(true),
    /**
     * Ist dieser Spieler der Testgegner (Abschnitt 97)?
     *
     * Der Client zeigt es an - man soll jederzeit wissen, ob man gegen einen
     * Menschen oder gegen den Rechner spielt.
     */
    isBot: t.boolean().default(false),
    /** Simulationstakt, in dem der Spieler beigetreten ist. */
    joinedAtTick: t.uint32().default(0),

    // --- Hero auf der vereinfachten 2D-Simulationsebene ---
    /** Position entlang der Lane. */
    x: t.float32().default(0),
    /** Position quer zur Lane. */
    y: t.float32().default(0),
    /** Blickrichtung im Bogenmass. */
    facing: t.float32().default(0),
    /** Bewegung in Welteinheiten pro Sekunde. */
    moveSpeed: t.float32().default(0),

    // --- Zustand des Heroes ---
    hp: t.float32().default(0),
    maxHp: t.float32().default(0),
    /** False, solange der Hero auf seinen Respawn wartet. */
    alive: t.boolean().default(true),
    /** Verbleibende Sekunden bis zum Respawn. 0, wenn der Hero lebt. */
    respawnInSeconds: t.float32().default(0),

    /**
     * Restliche Sterbephase in Sekunden. 0 = der Hero ist nicht mehr zu sehen.
     *
     * Ein gefallener Hero bleibt kurz liegen, wo es passiert ist, und
     * verschwindet dann bis zu seiner Rueckkehr (Produktentscheidung vom
     * 25.09.2026). Der Server fuehrt diese Zeit, damit beide Seiten denselben
     * Moment zeigen.
     */
    deathFadeSeconds: t.float32().default(0),

    /**
     * Wie lange der Hero noch an seine Faehigkeit gebunden ist. 0 = er ist
     * frei.
     *
     * Waehrend dieser Zeit steht er still. Es muss im Zustand stehen, weil der
     * Client seine Bewegung vorhersagt - sonst liefe die Figur im Bild los,
     * waehrend sie auf dem Server steht.
     *
     * Zwei Dinge zaehlen hier herunter, und das ist Absicht (Stand
     * 25.10.2026):
     *
     *   - ein KANAL ueber mehrere Sekunden (Barrage des Deadeye),
     *   - die kurze Sperre nach JEDEM Wirken (HERO_SKILL_LOCK_SECONDS):
     *     "Wenn man einen Skill macht, bleibt man stehen" (Robin).
     *
     * Ein neues Feld gab es dafuer nicht: PlayerState ist bei 63 Feldern
     * voll. Die Frage ist aber ohnehin dieselbe - "ist er gerade gebunden?" -,
     * und heroDarfLaufen beantwortet sie auf beiden Seiten gleich.
     */
    channelSeconds: t.float32().default(0),

    /**
     * Restliche Betaeubung in Sekunden. 0 = er kann handeln.
     *
     * Neu mit Hero gegen Hero (GAME_DESIGN Abschnitt 5b): Bis dahin
     * trafen Kontrolleffekte nur Creeps. Waehrend der Betaeubung nimmt
     * der Server keine Bewegung an und der Hero schlaegt nicht zu.
     *
     * Steht aus demselben Grund im Zustand wie channelSeconds: Der
     * Client sagt seine Bewegung voraus und muss dieselbe Sperre kennen.
     */
    stunnedFor: t.float32().default(0),

    /**
     * Restliches Tempo bei Verlangsamung, 0 bis 1. 1 = volle
     * Geschwindigkeit.
     *
     * Bewusst ein eigener Wert und nicht in moveSpeed eingerechnet:
     * moveSpeed wird bei jeder Aenderung an Items oder Stufen neu
     * gebildet - eine dort eingerechnete Verlangsamung waere beim
     * naechsten Kauf spurlos verschwunden.
     */
    slowFactor: t.float32().default(1),

    /**
     * Wo der Hero im Sanctuary-Ablauf steht (GAME_DESIGN Abschnitt 5f):
     * "draussen", "betritt", "drinnen" oder "verlaesst".
     *
     * Als Text und nicht als Zahl: Im Netzwerkverkehr macht das ein paar
     * Bytes aus, beim Lesen eines Zustands den Unterschied zwischen "2"
     * und "drinnen".
     */
    /**
     * Wie weit die eigene Armee Raum haelt - als Anteil der Lane, von
     * BLAUS Anchor aus gezaehlt (0 bis 1).
     *
     * Ein gemeinsames Mass fuer beide Spieler: Sonst muesste jede Stelle
     * wissen, aus wessen Sicht sie gerade liest. Gebraucht fuer Watch
     * Frontline und den Frontbalken im HUD (Abschnitt 13) - bis dahin
     * kannte die Linie nur der Server.
     */
    frontlineAt: t.float32().default(0),
    /** Haelt dieser Spieler ueberhaupt Raum? Sonst sagt frontlineAt nichts. */
    frontlineAktiv: t.boolean().default(false),

    sanctuary: t.string().default('draussen'),
    /**
     * Restzeit des laufenden Channels oder des Verlassens, in Sekunden.
     *
     * Muss im Zustand stehen: Der Client zeigt den Fortschritt an, und
     * seine Bewegungsvorhersage braucht dieselbe Sperre wie der Server.
     */
    sanctuarySeconds: t.float32().default(0),

    /**
     * Aufgestaute Stufen der Passive "Marked Prey" (Deadeye).
     *
     * Nur fuer die Anzeige: Der Spieler soll sehen, wie weit er auf seinem
     * aktuellen Ziel schon ist.
     */
    markStacks: t.uint8().default(0),

    /**
     * Aufgestaute Stufen der Passive "Momentum" (Shade).
     *
     * Sie wirken auf Tempo und Angriffsgeschwindigkeit. Im Zustand stehen sie,
     * damit die Anzeige sie zeigen kann - gerechnet wird ausschliesslich auf
     * dem Server.
     */
    momentumStacks: t.uint8().default(0),

    /**
     * So lange verlieren die Gegner ihn noch aus den Augen (Smoke Bomb).
     *
     * 0 = er ist ganz normal angreifbar.
     */
    hiddenFor: t.float32().default(0),

    // --- Ressource fuer Skills (Mana oder Energy, je nach Hero) ---
    mana: t.float32().default(0),
    maxMana: t.float32().default(0),

    /** Skills des Heroes, aufsteigend nach Slot. */
    skills: t.array(SkillState),

    // --- Level und Erfahrung (Abschnitte 45 bis 47) ---
    /** Aktuelles Hero-Level. Beginnt bei 1. */
    level: t.uint8().default(1),
    /** Gesamte gesammelte Erfahrung im Match. */
    xp: t.uint32().default(0),
    /**
     * Freie Skillpunkte.
     *
     * Sie duerfen aufgespart werden (Abschnitt 46). Ausgeben kann man sie im
     * Skilltree - der kommt als naechster Schritt.
     */
    skillPoints: t.uint8().default(0),
    /** Offene Talentwahl (Level 8). */
    talentPending: t.boolean().default(false),
    /** Offene Capstone-Wahl (Level 10). */
    capstonePending: t.boolean().default(false),
    /** Ausbaustufe je Grundskill. */
    skillUpgrades: t.ref(HeroSkillUpgrades).default(() => new HeroSkillUpgrades()),
    /** Gewaehltes Talent (Level 8). Leer = noch keins. */
    talentId: t.string().default(''),
    /** Gewaehlter Capstone (Level 10). Leer = noch keiner. */
    capstoneId: t.string().default(''),

    // --- Items (Abschnitt 66) ---
    /** Belegte Itemslots, hoechstens vier. */
    items: t.array(ItemState),
    /**
     * Traenke, die noch unterwegs sind (Produktentscheidung vom 05.10.2026).
     *
     * Sie zaehlen fuer die Obergrenze mit: Wer zwei bestellt hat, kann keinen
     * dritten kaufen, auch wenn noch keiner angekommen ist.
     */
    pendingPotions: t.array(PendingPotionState),
    /** Vorraetige Health Potions. Belegen keinen Slot. */
    healthPotions: t.uint8().default(0),
    /** Vorraetige Mana Potions. Belegen keinen Slot. */
    manaPotions: t.uint8().default(0),
    /** Gemeinsame Abklingzeit der Consumables in Sekunden. */
    potionCooldown: t.float32().default(0),

    /**
     * Restliche Wartezeit, bis wieder eine Welle gesendet werden darf.
     *
     * Produktentscheidung vom 24.09.2026: Wellen sind jetzt groesser, dafuer
     * seltener. Der Wert steht im Zustand, damit die Oberflaeche ihn anzeigen
     * kann - entschieden wird trotzdem auf dem Server.
     */
    /**
     * Restliche Pausenzeit dieses Spielers in Sekunden.
     *
     * Beginnt bei DISCONNECT_PAUSE_SECONDS und wird nur verbraucht, solange er
     * weg ist. Ist sie aufgebraucht, laeuft das Match ohne ihn weiter.
     */
    pauseBudgetRemaining: t.float32().default(0),

    /**
     * Gesendete Creeps in der Lane DIESES Spielers - auch die, die noch
     * erscheinen.
     *
     * Genau diese Zahl benutzt der Server fuer die Obergrenze. Sie steht im
     * Zustand, damit die Anzeige dieselbe Zahl zeigt und nicht ihre eigene
     * rechnet: Im Spiel aufgefallen (25.09.2026), dass die Anzeige direkt nach
     * dem Senden noch 0 zeigte, weil die Creeps unterwegs waren - und man
     * daraufhin eine Welle baute, die der Server dann ablehnte.
     */
    incomingCreeps: t.uint8().default(0),

    /**
     * Zaehler fuer gewirkte Skills.
     *
     * Er zaehlt bei jedem Einsatz hoch. Der Client erkennt daran, dass gerade
     * ein Skill gewirkt wurde, und blendet die getroffene Flaeche kurz ein -
     * dasselbe Verfahren wie bei den normalen Angriffen (lastAttackSeq).
     */
    lastSkillSeq: t.uint32().default(0),
    /** Slot des zuletzt gewirkten Skills (1 bis 4). */
    lastSkillSlot: t.uint8().default(0),
    /** Zielpunkt des zuletzt gewirkten Skills. */
    lastSkillX: t.float32().default(0),
    lastSkillY: t.float32().default(0),
    /** Standort des Heroes beim Wirken - noetig fuer Kegel und Bahn. */
    lastSkillOriginX: t.float32().default(0),
    lastSkillOriginY: t.float32().default(0),

    /**
     * Ladestand der Passive des Arcanist ("Sap Surge"), 0 bis 1.
     *
     * Bei 1 ist der naechste Skill kostenlos und staerker. Steht im Zustand,
     * damit die Oberflaeche es anzeigen kann - gefuellt wird er auf dem Server.
     */
    overflowCharge: t.float32().default(0),

    waveCooldown: t.float32().default(0),

    /**
     * Gesamte Schadensreduktion, 0 bis 1.
     * Setzt sich aus der Passive und laufenden Skilleffekten zusammen und wird
     * fuer die Anzeige uebertragen - gerechnet wird ausschliesslich serverseitig.
     */
    damageReduction: t.float32().default(0),
    /** Restlaufzeit des Ultimate in Sekunden. 0 = nicht aktiv. */
    ultimateActiveFor: t.float32().default(0),

    /**
     * Laufende Nummer des letzten Angriffs.
     *
     * Der Client erkennt an einer Aenderung, dass gerade zugeschlagen wurde,
     * und zeigt es an: Nahkaempfer holen kurz aus, Fernkaempfer zeigen eine
     * Schussspur. Ohne diesen Wert saehe man nur die sinkenden Lebensbalken -
     * aber nie, WER gerade angreift.
     */
    lastAttackSeq: t.uint32().default(0),

    /**
     * Gegner, den der Hero gerade angreifen wuerde.
     *
     * Der Client umrandet ihn. Das ist vor allem dann wichtig, wenn ein
     * Fernkaempfer aus der Entfernung auf den Hero schiesst: Man sieht dann
     * sofort, WER einen angreift, und kann hinlaufen.
     */
    autoTargetId: t.string().default(''),

    /*
     * Portal, das dieser Spieler verteidigt.
     *
     * uint16 und nicht uint8 (30.09.2026): Mit 8 Bit war bei 255 Schluss, und
     * ein hoeher gesetztes PORTAL_MAX_HP kam beim Client als Rest der Division
     * durch 256 an - aus 340 wurden 84. Es fiel nur auf, weil ein Test den
     * Wert gegen PORTAL_MAX_HP geprueft hat.
     *
     * Das Portalleben ist ein Balancewert und soll frei waehlbar sein, nicht
     * von der Breite eines Feldes begrenzt. uint16 reicht bis 65535; ein Byte
     * je Spieler und Aktualisierung ist dafuer ein guenstiger Preis.
     */
    portalHp: t.uint16().default(0),

    // --- Wirtschaft (Masterprompt Abschnitt 63) ---
    // Es gibt bewusst weder Gold- noch Incomelimit. uint32 reicht bis ueber vier
    // Milliarden - weit jenseits dessen, was ein Match je erreicht.
    //
    // GEHEIM VOR DEM GEGNER (Abschnitt 83): Gold und Income sieht nur der
    // Spieler selbst. Das Kennzeichen .view() sorgt dafuer, dass der Server
    // diese Felder ausschliesslich an den Besitzer uebertraegt. Es reicht
    // nicht, sie nur in der Oberflaeche auszublenden - ein manipulierter Client
    // koennte sonst mitlesen.
    /** Aktueller Goldstand. Nur fuer den Besitzer sichtbar. */
    gold: t.uint32().default(0).view(),
    /** Gold pro Income-Tick (alle 15 Sekunden). Nur fuer den Besitzer sichtbar. */
    income: t.uint32().default(0).view(),
    /** Freigeschaltetes Creep-Tier, 1 bis 4. */
    tier: t.uint8().default(1),

    /**
     * Hat dieser Spieler nach dem Matchende ein neues Match angefragt?
     * Beide muessen zustimmen - dann wird das Match zurueckgesetzt.
     */
    lobbyReady: t.boolean().default(false),

    /**
     * Hat dieser Spieler seinen Linebound SELBST gewaehlt?
     *
     * heroId ist nie leer - sonst gaebe es einen Spieler ohne Werte. Beim
     * Beitreten steht deshalb eine Vorbelegung darin, und dieses Feld sagt,
     * ob sie jemand bestaetigt hat.
     *
     * Zwei Dinge haengen daran (13.10.2026): Der Gegner sieht in der Lobby,
     * ob schon gewaehlt wurde - und wer bis zum Ablauf der Uhr nicht
     * gewaehlt hat, bekommt einen zufaelligen.
     */
    heroChosen: t.boolean().default(false),

    /**
     * Der am weitesten vorn liegende x-Wert, den dieser Hero erreichen darf
     * (GAME_DESIGN Abschnitt 5a, ab 08.10.2026).
     *
     * Er steht im Zustand und nicht nur im Server, weil der Client mit
     * derselben Funktion vorausrechnet: Ohne die Grenze wuerde seine
     * Vorhersage an der Leash dauernd vom Server korrigiert, und die
     * Steuerung ruckelte genau dort, wo es eng wird.
     *
     * Zugleich zeichnet der Client daraus die Energielinie, die kurz vor der
     * Grenze sichtbar wird.
     */
    leashX: t.float32().default(0),
  },
  'PlayerState',
);
export type PlayerState = SchemaType<typeof PlayerState>;

/**
 * Ein angreifender Creep in der Lane eines Spielers.
 *
 * Die Kampfwerte selbst stehen NICHT hier, sondern in @dueline/game-data.
 * Uebertragen wird nur, was sich veraendert - das spart auf dem Handy spuerbar
 * Datenmenge.
 */
export const CreepState = schema(
  {
    /** Eindeutige Kennung dieser einen Einheit. */
    id: t.string(),
    /** ID der Creep-Art, z. B. "minion". Schluessel in @dueline/game-data. */
    defId: t.string(),
    /**
     * Stammt dieser Creep aus einer gesendeten Welle?
     *
     * Nur solche zaehlen gegen die Obergrenze der Lane. Was im Kampf selbst
     * entsteht - Splitter, Beschworene - zaehlt nicht mit: Sonst blockiert ein
     * Necromancer dem Gegner nach und nach das Senden, ohne dass jemand etwas
     * dafuer kann (im Spiel aufgefallen am 25.09.2026: Anzeige stand auf
     * 36/25).
     */
    fromWave: t.boolean().default(false),
    /** Sitzungs-ID des Spielers, dessen Lane angegriffen wird. */
    laneOwner: t.string(),

    /** Position auf der vereinfachten 2D-Simulationsebene (lane-lokal). */
    x: t.float32().default(0),
    y: t.float32().default(0),
    /** Blickrichtung im Bogenmass. */
    facing: t.float32().default(0),

    hp: t.float32().default(0),
    maxHp: t.float32().default(0),

    /** march | chase | attack | return - steuert spaeter die Animation. */
    mode: t.string().default('march'),

    /**
     * Laufende Nummer des letzten Angriffs.
     *
     * Der Client erkennt an einer Aenderung, dass gerade zugeschlagen wurde,
     * und zeigt es an: Nahkaempfer holen kurz aus, Fernkaempfer zeigen eine
     * Schussspur. Ohne diesen Wert saehe man nur die sinkenden Lebensbalken -
     * aber nie, WER gerade angreift.
     */
    lastAttackSeq: t.uint32().default(0),
    /**
     * Laufende Nummer der letzten Heilung.
     *
     * Wie lastAttackSeq, nur fuer Heiler: Aendert sich die Zahl, hat diese
     * Einheit gerade geheilt, und der Client zeigt einen gruenen Ring in
     * Heilreichweite. Ohne das sieht man nur, dass irgendwo Leben zurueckkommt
     * - aber nicht, wer dafuer sorgt.
     */
    lastHealSeq: t.uint32().default(0),
    /** Restliche Betaeubung in Sekunden. 0 = nicht betaeubt. */
    stunnedFor: t.float32().default(0),
    /**
     * Verbleibende Bewegung waehrend einer Verlangsamung, 0 bis 1.
     * 1 = normale Geschwindigkeit.
     */
    slowFactor: t.float32().default(1),
  },
  'CreepState',
);
export type CreepState = SchemaType<typeof CreepState>;

/**
 * Ein beschworener Begleiter (ab 08.10.2026).
 *
 * Hiess bis dahin DefenderState und trug die vier kaufbaren Defender. Mit
 * dem Defense System sind die weggefallen (GAME_DESIGN Abschnitt 10);
 * uebrig ist genau eine Einheit: das Schattenbild des Shade.
 *
 * Was mit dem Defender wegfiel: defensePoints (es gibt kein Budget mehr)
 * und lastHealSeq (nur der Support heilte).
 */
export const BegleiterState = schema(
  {
    id: t.string(),
    /** Welche Art Begleiter. Bisher immer "shadow". */
    begleiterId: t.string(),
    /** Wem er gehoert - und wessen Gegner er angreift. */
    ownerSessionId: t.string(),

    x: t.float32().default(0),
    y: t.float32().default(0),
    facing: t.float32().default(0),

    hp: t.float32().default(0),
    maxHp: t.float32().default(0),
    /** Verbleibende Lebensdauer in Sekunden - der Skill gibt sie vor. */
    secondsRemaining: t.float32().default(0),
    /** advance | chase | attack | hold - nur fuer die Darstellung. */
    mode: t.string().default('advance'),
    /**
     * Laufende Nummer des letzten Angriffs.
     *
     * Der Client erkennt an einer Aenderung, dass gerade zugeschlagen wurde,
     * und zeigt es an: Nahkaempfer holen kurz aus, Fernkaempfer zeigen eine
     * Schussspur. Ohne diesen Wert saehe man nur die sinkenden Lebensbalken -
     * aber nie, WER gerade angreift.
     */
    lastAttackSeq: t.uint32().default(0),
  },
  'BegleiterState',
);
export type BegleiterState = SchemaType<typeof BegleiterState>;

/** Der gesamte Zustand eines Matches. */
export const MatchState = schema(
  {
    /** Eindeutige Match-ID, taucht in allen Serverlogs auf (Abschnitt 91). */
    matchId: t.string(),
    /** Aktuelle Matchphase. */
    phase: t.string().default('waiting'),
    /**
     * Restzeit der laufenden Phase in Sekunden.
     * Nur waehrend Vorbereitung und Countdown groesser als 0.
     */
    phaseSecondsRemaining: t.float32().default(0),
    /** Fortlaufender Simulationstakt seit Raumstart. */
    tick: t.uint32().default(0),
    /** Verstrichene Matchzeit in Sekunden. */
    elapsedSeconds: t.float32().default(0),
    /** Simulationstakte pro Sekunde, mit denen dieser Raum rechnet. */
    tickRate: t.uint8().default(20),
    /** Alle Spieler, Schluessel ist die sessionId. */
    players: t.map(PlayerState),
    /** Alle aktiven Creeps beider Lanes, Schluessel ist die Creep-ID. */
    creeps: t.map(CreepState),
    /** Alle lebenden Begleiter beider Seiten, Schluessel ist ihre ID. */
    begleiter: t.map(BegleiterState),
    /** Laufende Bodeneffekte beider Lanes. */
    groundEffects: t.map(GroundEffectState),
    /**
     * Beitrittscode eines privaten Matches (Abschnitt 88).
     *
     * Leer bei einem Schnellmatch - dort gibt es nichts weiterzugeben.
     */
    matchCode: t.string().default(''),
    /**
     * Sitzungs-ID des Spielers, wegen dem das Match gerade pausiert.
     *
     * Leer, solange alles normal laeuft. Beide Seiten sehen daran, warum
     * nichts weitergeht - und wie lange es hoechstens noch dauert.
     */
    pausedForSessionId: t.string().default(''),
    /** Verbleibende Pausenzeit in Sekunden, 0 wenn nicht pausiert. */
    pauseSecondsRemaining: t.float32().default(0),
    /** Sitzungs-ID des Siegers, sobald das Match entschieden ist. */
    winnerSessionId: t.string(),
  },
  'MatchState',
);
export type MatchState = SchemaType<typeof MatchState>;
